// ============================================================
//  LinkWhatsAppModal.js — link the owner's WhatsApp to Contact Saver
//  QR code (scan from WhatsApp → Linked devices) or an 8-letter pairing code.
//  Polls every 2 s until linked, expired or failed.
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { contactSaverAction } from '../../api/client';

export default function LinkWhatsAppModal({ acc, label, onClose, onLinked }) {
  const [method, setMethod] = useState('qr');          // qr | code
  const [phone, setPhone] = useState('');
  const [link, setLink] = useState({ state: 'idle' });
  const [err, setErr] = useState('');
  const timer = useRef(null);
  const live = useRef(true);
  const stateRef = useRef('idle');
  stateRef.current = link.state;
  const active = ['starting', 'qr', 'code', 'syncing'].includes(link.state);

  const stopPolling = () => { clearTimeout(timer.current); timer.current = null; };
  async function poll() {
    try {
      const res = await contactSaverAction('link_status', { acc });
      if (!live.current) return;
      const l = res.data.link;
      stateRef.current = l.state;
      setLink(l);
      if (l.state === 'linked' || (res.data.account?.linked && l.state === 'idle')) {
        toast.success('WhatsApp linked — new contacts will be saved automatically', { icon: '✅' });
        onLinked();
        return;
      }
      if (['starting', 'qr', 'code', 'syncing'].includes(l.state)) timer.current = setTimeout(poll, 2000);
    } catch (e) {
      if (!live.current) return;
      setErr(e?.response?.data?.message || 'Lost touch with the linker. Try again.');
      setLink({ state: 'failed' });
    }
  }

  async function start() {
    stopPolling(); setErr('');
    if (method === 'code' && phone.replace(/\D/g, '').length < 10) { setErr('Enter your WhatsApp number, e.g. 08031234567'); return; }
    setLink({ state: 'starting' });
    try {
      const res = await contactSaverAction('link_start', method === 'code' ? { acc, phone } : { acc });
      setLink(res.data.link);
      timer.current = setTimeout(poll, 1500);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not start linking');
      setLink({ state: 'failed' });
    }
  }

  // QR starts straight away; the phone-number way waits for the number
  useEffect(() => { if (method === 'qr') start(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [method]);
  useEffect(() => { live.current = true; return () => {
    live.current = false; stopPolling();
    // closing mid-scan cancels it; once linked (or saving the contact list) we let it finish
    if (['starting', 'qr', 'code'].includes(stateRef.current)) contactSaverAction('link_cancel', { acc }).catch(() => {});
  }; }, []);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const code = link.code ? `${link.code.slice(0, 4)}-${link.code.slice(4)}` : '';
  const shownErr = err || link.error;

  return (
    <div className="csv-modal" role="dialog" aria-modal="true" aria-labelledby="csv-link-title" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="csv-modal-box">
        <div className="csv-modal-head">
          <h2 id="csv-link-title">Link WhatsApp{label ? ` · ${label}` : ''}</h2>
          <button type="button" className="csv-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>

        <div className="csv-seg csv-seg-wide" role="tablist" aria-label="How to link">
          <button type="button" role="tab" aria-selected={method === 'qr'} className={method === 'qr' ? 'is-on' : ''} onClick={() => setMethod('qr')}>Scan QR code</button>
          <button type="button" role="tab" aria-selected={method === 'code'} className={method === 'code' ? 'is-on' : ''} onClick={() => { if (method === 'code') return; stopPolling(); contactSaverAction('link_cancel', { acc }).catch(() => {}); setErr(''); setLink({ state: 'idle' }); setMethod('code'); }}>Use phone number</button>
        </div>

        <div className="csv-link">
          <ol className="csv-steps">
            <li><b>1</b><span>Open WhatsApp on your phone</span></li>
            <li><b>2</b><span>Tap <b>⋮</b> or <b>Settings</b> → <b>Linked devices</b> → <b>Link a device</b></span></li>
            {method === 'qr'
              ? <li><b>3</b><span>Point your phone at this code</span></li>
              : <li><b>3</b><span>Tap <b>Link with phone number instead</b> and type the code below</span></li>}
          </ol>

          <div className="csv-link-pane" aria-live="polite">
            {method === 'code' && !active && link.state !== 'linked' && (
              <div className="csv-code-form">
                <label className="csv-field">Your WhatsApp number
                  <input inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="08031234567" onKeyDown={e => { if (e.key === 'Enter') start(); }} />
                </label>
                <button type="button" className="csv-btn csv-btn-dark" onClick={start}>Get code</button>
              </div>
            )}
            {link.state === 'starting' && <div className="csv-wait"><span className="csv-spin" /> Getting a code from WhatsApp…</div>}
            {link.state === 'qr' && link.qr && <img className="csv-qr" src={link.qr} alt="QR code — scan it with WhatsApp on your phone" />}
            {link.state === 'code' && code && <div className="csv-code" aria-label={`Pairing code ${link.code.split('').join(' ')}`}>{code}</div>}
            {link.state === 'syncing' && <div className="csv-wait"><span className="csv-spin" /> Linked! Reading who’s already in your contacts…</div>}
            {shownErr && ['failed', 'expired'].includes(link.state) && (
              <div className="csv-link-err">
                <p>{shownErr}</p>
                <button type="button" className="csv-btn csv-btn-dark csv-btn-sm" onClick={start}><i className="fa-solid fa-rotate-right" /> {method === 'qr' ? 'New code' : 'Try again'}</button>
              </div>
            )}
            {shownErr && link.state === 'idle' && <p className="csv-link-err">{shownErr}</p>}
          </div>
        </div>
        <p className="csv-fine">CommandKit only sees <b>who</b> messaged you — never what they said. You can unlink any time from here or from WhatsApp → Linked devices.</p>
      </div>
    </div>
  );
}
